import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, GraduationCap, Users, Flame, Star, BookOpen, CheckSquare, ArrowRight, Sparkles, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { formatLevel } from '@/lib/levels';
import LamnsoDateDisplay from '@/components/LamnsoDate';
import { BetaOnboardingModal, BetaInfoCard, shouldShowOnboarding, markOnboardingSeen } from '@/components/BetaOnboarding';
import Walkthrough, { shouldShowWalkthrough } from '@/components/Walkthrough';
import api from '@/lib/api';

const TAABIY = 'https://customer-assets.emergentagent.com/job_lamnso-hub/artifacts/ed75ipfo_Ta%C3%A0biy.png';
const YEEBIY = 'https://customer-assets.emergentagent.com/job_lamnso-hub/artifacts/49uszg6d_Ye%C3%A8biy.png';

export default function Home() {
  const { user, isAuthenticated, mustChangePassword } = useAuth();
  const [profile, setProfile] = useState(null);
  const [featured, setFeatured] = useState(null);
  const [stats, setStats] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [showProficiencyPicker, setShowProficiencyPicker] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      api.get('/users/profile').then(r => {
        setProfile(r.data);
        // If Google user without proficiency set, show picker (but not during password change)
        if (r.data && !r.data.proficiency_self_assessed && r.data.auth_provider === 'google' && !mustChangePassword) {
          setShowProficiencyPicker(true);
        }
      }).catch(err => console.error(err));
    }
    api.get('/translations/search?q=love&limit=10').then(r => {
      const trans = r.data.translations || [];
      // Prefer koŋ as the featured word for "love"
      const kong = trans.find(t => t.lamnso_word?.text === 'koŋ');
      if (kong) setFeatured(kong);
      else if (trans[0]) setFeatured(trans[0]);
    }).catch(() => {});
    api.get('/lessons/units').then(r => setStats(r.data)).catch(() => {});
  }, [isAuthenticated, mustChangePassword]);

  // Show onboarding modal on first visit (but not during password change)
  useEffect(() => {
    if (user?.user_id && shouldShowOnboarding(user.user_id) && !mustChangePassword) {
      setShowOnboarding(true);
    }
  }, [user, mustChangePassword]);

  const handleCloseOnboarding = () => {
    setShowOnboarding(false);
    if (user?.user_id) markOnboardingSeen(user.user_id);
    // Start walkthrough after beta onboarding dismisses
    if (user?.user_id && shouldShowWalkthrough(user.user_id)) {
      setTimeout(() => setShowWalkthrough(true), 500);
    }
  };

  // If no onboarding modal but walkthrough hasn't been seen
  useEffect(() => {
    if (user?.user_id && !showOnboarding && !mustChangePassword && shouldShowWalkthrough(user.user_id)) {
      // Only show if onboarding was already seen (returning user who hasn't done walkthrough)
      if (!shouldShowOnboarding(user.user_id)) {
        setTimeout(() => setShowWalkthrough(true), 1000);
      }
    }
  }, [user, showOnboarding, mustChangePassword]); // eslint-disable-line

  const assistant = user?.name?.length % 2 === 0 ? TAABIY : YEEBIY;
  const assistantName = assistant === TAABIY ? 'Taàbiy' : 'Yeèbiy';
  const greeting = getGreeting();
  const xp = profile?.xp_points || 0;
  const streak = profile?.streak_count || 0;
  const level = profile?.level || 1;

  return (
    <div className="max-w-lg mx-auto px-4 pt-4 pb-20 sm:max-w-2xl md:max-w-3xl">

      {/* Beta Onboarding Modal */}
      {showOnboarding && <BetaOnboardingModal user={user} onClose={handleCloseOnboarding} />}

      {/* Proficiency picker for Google users */}
      {showProficiencyPicker && !showOnboarding && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <h2 className="text-lg font-bold text-[#1C1917] mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>What's your Lamnso' level?</h2>
            <p className="text-xs text-[#57534E] mb-4">This helps personalize your experience.</p>
            <div className="space-y-2">
              {[
                { key: 'beginner', level: 1, title: "Wán (Beginner)", desc: "New to Lamnso'" },
                { key: 'speaker', level: 2, title: "Wà'bìn (Speaker)", desc: "Can speak some Lamnso'" },
                { key: 'storyteller', level: 3, title: "Ránsísí (Storyteller)", desc: "Fluent in Lamnso'" },
                { key: 'elder', level: 4, title: "Wirfon (Elder)", desc: "Deep cultural knowledge" },
              ].map(p => (
                <button key={p.key} onClick={async () => {
                  try {
                    await api.post('/users/set-proficiency', { proficiency: p.key });
                    setShowProficiencyPicker(false);
                    api.get('/users/profile').then(r => setProfile(r.data)).catch(err => console.error(err));
                  } catch (err) { console.error(err); }
                }} className="w-full text-left p-3 rounded-xl border-2 border-[#E7E5E4] hover:border-[#4CAF50] active:bg-[#E8F5E9] transition-all">
                  <p className="text-sm font-semibold text-[#1C1917]">{p.title}</p>
                  <p className="text-[10px] text-[#57534E]">{p.desc}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Interactive Walkthrough */}
      {showWalkthrough && !showOnboarding && (
        <Walkthrough user={user} onComplete={() => setShowWalkthrough(false)} />
      )}

      {/* Assistant greeting */}
      <div className="flex items-end gap-3 mb-5 animate-fade-in-up" data-testid="home-greeting">
        <div className="w-16 h-16 flex-shrink-0 animate-bounce-gentle">
          <img src={assistant} alt={assistantName} className="w-full h-full object-contain drop-shadow-md" />
        </div>
        <div className="bg-white rounded-2xl rounded-bl-md border border-[#E7E5E4] px-4 py-3 shadow-sm flex-1">
          <p className="text-sm text-[#1C1917] font-medium">
            {isAuthenticated
              ? `${greeting}, ${user?.name?.split(' ')[0]}! ${getEncouragement(streak, xp)}`
              : `${greeting}! Welcome to LámFon — your home for learning Lamnso'.`
            }
          </p>
          <p className="text-[10px] text-[#A8A29E] mt-0.5">— {assistantName}</p>
        </div>
      </div>

      {/* Date + Level strip */}
      <div className="flex items-center justify-between mb-5 animate-fade-in-up stagger-1">
        <LamnsoDateDisplay />
        {isAuthenticated && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-xs font-semibold text-[#EA580C]" data-testid="home-streak">
              <Flame className="w-4 h-4" /> {streak}
            </div>
            <div className="flex items-center gap-1 text-xs font-semibold text-[#4CAF50]" data-testid="home-xp">
              <Star className="w-4 h-4" /> {xp}
            </div>
            <span className="text-[10px] font-bold text-[#D4A373] bg-[#FDF6F0] px-2 py-0.5 rounded-full" data-testid="home-level">
              {formatLevel(level, 'short')}
            </span>
          </div>
        )}
      </div>

      {/* Quick search */}
      <Link to="/search" className="block mb-5 animate-fade-in-up stagger-2" data-testid="home-search">
        <div className="flex items-center gap-3 bg-white rounded-2xl border border-[#E7E5E4] px-4 py-3.5 shadow-sm hover:shadow-md transition-shadow">
          <Search className="w-5 h-5 text-[#A8A29E]" />
          <span className="text-sm text-[#A8A29E]">Search Lamnso' or English...</span>
        </div>
      </Link>

      {/* Beta info card (dismissible, revisitable) */}
      {isAuthenticated && <BetaInfoCard user={user} />}

      {/* Action cards */}
      <div className="grid grid-cols-2 gap-3 mb-5 animate-fade-in-up stagger-2">
        <ActionCard to="/learn" icon={<GraduationCap className="w-5 h-5 text-[#4CAF50]" />}
          title="Continue Learning" subtitle={stats?.units?.[0] ? `${stats.units.length} units` : 'Start lessons'}
          color="bg-[#E8F5E9]" testId="home-learn-card" />
        <ActionCard to="/practice" icon={<Sparkles className="w-5 h-5 text-[#D4A373]" />}
          title="Practice Reading" subtitle="Bilingual sentences"
          color="bg-[#FDF6F0]" testId="home-practice-card" />
        {isAuthenticated && (
          <>
            <ActionCard to="/community" icon={<Users className="w-5 h-5 text-[#588157]" />}
              title="Community" subtitle="Questions & stories"
              color="bg-[#EAF1EA]" testId="home-community-card" />
            {level >= 4 ? (
              <ActionCard to="/validate" icon={<CheckSquare className="w-5 h-5 text-[#D4A373]" />}
                title="Wirfon Review" subtitle="Validate translations"
                color="bg-[#FDF6F0]" testId="home-validate-card" />
            ) : (
              <ActionCard to="/search" icon={<BookOpen className="w-5 h-5 text-[#78716C]" />}
                title="Translate" subtitle="13,000+ translations"
                color="bg-[#F5F5F4]" testId="home-dict-card" />
            )}
          </>
        )}
        {isAuthenticated && user?.role === 'admin' && (
          <ActionCard to="/admin" icon={<Shield className="w-5 h-5 text-[#6D28D9]" />}
            title="Admin Dashboard" subtitle="Manage app content"
            color="bg-[#F3F0FF]" testId="home-admin-card" />
        )}
        {!isAuthenticated && (
          <>
            <ActionCard to="/search" icon={<BookOpen className="w-5 h-5 text-[#78716C]" />}
              title="Translate" subtitle="13,000+ translations"
              color="bg-[#F5F5F4]" testId="home-dict-card" />
            <ActionCard to="/register" icon={<Users className="w-5 h-5 text-[#0891B2]" />}
              title="Join LámFon" subtitle="Free account"
              color="bg-[#ECFEFF]" testId="home-join-card" />
          </>
        )}
      </div>

      {/* Progress card (logged in) */}
      {isAuthenticated && profile && (
        <div className="bg-white rounded-2xl border border-[#E7E5E4] p-4 mb-5 animate-fade-in-up stagger-3" data-testid="home-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-[#1C1917]">{formatLevel(level, 'combined')}</span>
            <span className="text-[10px] text-[#A8A29E]">{xp} / {profile.next_level_xp || 100} XP</span>
          </div>
          <Progress value={Math.min(((xp - (profile.current_level_xp || 0)) / Math.max((profile.next_level_xp || 100) - (profile.current_level_xp || 0), 1)) * 100, 100)} className="h-2.5 bg-[#F5F5F4]" />
        </div>
      )}

      {/* Featured word */}
      {featured && (
        <div className="bg-white rounded-2xl border border-[#E7E5E4] p-4 mb-5 animate-fade-in-up stagger-3" data-testid="home-featured">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold text-[#A8A29E] uppercase tracking-wider">Featured Word</span>
            <Link to={`/search?q=${encodeURIComponent(featured.english_word?.text || '')}`} className="text-[10px] text-[#4CAF50] font-semibold">
              See more <ArrowRight className="w-3 h-3 inline" />
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xl font-bold text-[#1C1917]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {featured.lamnso_word?.text || '?'}
            </span>
            <ArrowRight className="w-4 h-4 text-[#A8A29E]" />
            <span className="text-lg text-[#4CAF50] font-semibold">{featured.english_word?.text || '?'}</span>
          </div>
        </div>
      )}

      {/* Not logged in CTA */}
      {!isAuthenticated && (
        <div className="bg-gradient-to-br from-[#E8F5E9] to-[#FDF6F0] rounded-2xl p-5 text-center animate-fade-in-up stagger-4" data-testid="home-cta">
          <img src={YEEBIY} alt="Yeèbiy" className="w-20 h-20 mx-auto mb-3 object-contain animate-bounce-gentle" />
          <h2 className="text-lg font-bold text-[#1C1917]" style={{ fontFamily: "'Outfit', sans-serif" }}>Start Your Lamnso' Journey</h2>
          <p className="text-xs text-[#57534E] mt-1 mb-4">Track progress, earn XP, and join the community</p>
          <Link to="/register">
            <Button className="bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl px-6" data-testid="home-signup-btn">
              Create Free Account
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}

function ActionCard({ to, icon, title, subtitle, color, testId }) {
  return (
    <Link to={to} className="block" data-testid={testId}>
      <div className={`${color} rounded-2xl p-4 hover:shadow-md transition-all active:scale-[0.98] h-full`}>
        <div className="mb-2">{icon}</div>
        <p className="text-sm font-semibold text-[#1C1917] leading-tight">{title}</p>
        <p className="text-[10px] text-[#57534E] mt-0.5">{subtitle}</p>
      </div>
    </Link>
  );
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function getEncouragement(streak, xp) {
  if (streak >= 7) return "Amazing streak! Keep it going!";
  if (streak >= 3) return "You're on a roll!";
  if (xp >= 100) return "Great progress so far!";
  return "Ready to learn something new?";
}
